import React, { useState } from 'react'
import log from 'loglevel'
import { RpcError } from 'grpc-web'
import { useNavigate, useParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import {
  useHideModal,
  useIsShowModal,
  useModalState,
} from 'src/Modal/ModalStateContext'
import { useCustomFrameworkWizard } from 'src/pages/Frameworks/context/CustomFrameworkWizardContext'
import { useCreateCustomFrameworkWithRequirements } from 'src/pages/Frameworks/frameworks.hooks'
import { FrameworksAbsoluteRoutes } from 'src/components/Reusable/RootPage/RootPage.constants'
import { getErrorMessage } from 'src/Utils/globalHelpers'
import { FlexAlign, FlexColumn } from 'src/components/Reusable/Flex'
import { P } from 'src/components/Reusable/Text/Text.styles'
import { MediumCancelButton } from 'src/components/Reusable/Buttons'
import { ReactComponent as AddIcon } from 'src/components/Icons/assets/add-icon.svg'
import { ReactComponent as BringIntoIcon } from 'src/components/Icons/assets/bring-into.svg'
import { ModalForm, ModalFormId } from '../..'
import { ModalButton, ModalButtonContainer } from '../../ModalForm.styles'

type OptionCard = {
  key: string
  title: string
  description: string
  icon: JSX.Element
  targetModalId: ModalFormId
}

export const AddUploadFrameworkRequirementsModal = (): JSX.Element => {
  const show = useIsShowModal(ModalFormId.ADD_UPLOAD_FRAMEWORK_REQUIREMENTS)
  const hide = useHideModal({
    modalId: ModalFormId.ADD_UPLOAD_FRAMEWORK_REQUIREMENTS,
  })
  const { openModal } = useModalState()
  const navigate = useNavigate()
  const { pageContext } = useParams()
  const submitWithRequirements = useCreateCustomFrameworkWithRequirements()
  const { name, description, groups, appendComplianceFrameworkModelId, reset } =
    useCustomFrameworkWizard()
  const [isSkipping, setIsSkipping] = useState(false)
  const isCreateFlow = !appendComplianceFrameworkModelId

  const cards: OptionCard[] = [
    {
      key: 'csv',
      title: 'Upload CSV',
      description:
        'Bulk import requirements from a CSV file. Download a template to get started.',
      icon: <BringIntoIcon />,
      targetModalId: ModalFormId.UPLOAD_FRAMEWORK_REQUIREMENTS_CSV,
    },
    {
      key: 'manual',
      title: 'Add manually',
      description:
        'Enter each requirement in a form. Control mapping is optional for any row.',
      icon: <AddIcon />,
      targetModalId: ModalFormId.ADD_FRAMEWORK_REQUIREMENTS,
    },
  ]

  const onSelect = (targetModalId: ModalFormId) => {
    hide()
    openModal(targetModalId)
  }

  const onBack = () => {
    hide()
    openModal(ModalFormId.ADD_CUSTOM_COMPLIANCE_FRAMEWORK)
  }

  const onClose = () => {
    reset()
    hide()
  }

  const onSkip = async () => {
    setIsSkipping(true)
    try {
      const { complianceFrameworkRecordId } = await submitWithRequirements({
        name: name.trim(),
        description,
        requirements: [],
        appendComplianceFrameworkModelId,
        groups,
      })
      reset()
      hide()
      navigate(
        `/${pageContext}/${FrameworksAbsoluteRoutes.SHOW}/${complianceFrameworkRecordId}`,
      )
    } catch (err) {
      const errorMessage = getErrorMessage(err as RpcError)
      toast.error(errorMessage.message)
      if (errorMessage.shouldLog) {
        log.error('AddUploadFrameworkRequirementsModal skip failed', err)
      }
    } finally {
      setIsSkipping(false)
    }
  }

  return (
    <ModalForm
      show={show}
      hide={onClose}
      formId={ModalFormId.ADD_UPLOAD_FRAMEWORK_REQUIREMENTS}
      size="xl"
      title="Add Requirements"
      description="Pick how you'd like to add requirements to this framework."
      hideButtons={!isCreateFlow}
      onBack={isCreateFlow ? onBack : undefined}
      hideCancel
      customSubmitButton={
        isCreateFlow ? (
          <MediumCancelButton onClick={onSkip} isDisabled={isSkipping}>
            Skip
          </MediumCancelButton>
        ) : (
          <></>
        )
      }
    >
      <form id={ModalFormId.ADD_UPLOAD_FRAMEWORK_REQUIREMENTS}>
        <ModalButtonContainer>
          {cards.map((card) => (
            <ModalButton
              key={card.key}
              onClick={() => onSelect(card.targetModalId)}
            >
              {card.icon}
              <FlexColumn justify={FlexAlign.FLEX_END} align={FlexAlign.CENTER}>
                <p>{card.title}</p>
                <P $isDim>{card.description}</P>
              </FlexColumn>
            </ModalButton>
          ))}
        </ModalButtonContainer>
      </form>
    </ModalForm>
  )
}
